import cn from 'classnames';
import momentTz from 'moment-timezone';
import React, { useMemo } from 'react';
import StatusButton from '~components/navigation-panel/status-button';
import Typography from '~components/typography';
import {
  BookingStatusOption,
  PaymentItemStatusOptions,
  StudentPayToOptions,
} from '~graphql/__generated__/globalTypes';
import { SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings } from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { useTranslation } from '~i18n';
import { Close } from '~svg-components';
import currencyformatter from '~utils/currency-format';
import getPaymentName from '~utils/get-payment-name';

interface IProps {
  booking: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings;
}

const BookingCardPayment: React.FC<IProps> = ({ booking }) => {
  const { t } = useTranslation(['student_portal']);

  const { status, paymentItems, nominationPlan, property } = booking;

  const isPayToPartner = nominationPlan?.studentPayTo === StudentPayToOptions.PARTNER;

  const paymentItemsAllPaid = useMemo(() => {
    return paymentItems.every(item => {
      return (
        item?.status === PaymentItemStatusOptions.PAID ||
        item?.status === PaymentItemStatusOptions.VOID ||
        item?.status === PaymentItemStatusOptions.PENDING ||
        item?.status === PaymentItemStatusOptions.WAIVED ||
        item?.status === PaymentItemStatusOptions.REFUNDED
      );
    });
  }, [paymentItems]);

  const paymentItemsUnPaid = useMemo(() => {
    const unpaidStatuses = [
      PaymentItemStatusOptions.OPEN,
      PaymentItemStatusOptions.INVOICED,
      PaymentItemStatusOptions.REMAINING,
      PaymentItemStatusOptions.OVERDUE,
      PaymentItemStatusOptions.AWAITING,
    ];

    return paymentItems
      .filter(item => item?.status && unpaidStatuses.includes(item.status))
      .sort(
        (a, b) => new Date(a?.invoiceDatetime).getTime() - new Date(b?.invoiceDatetime).getTime()
      );
  }, [paymentItems]);

  const unPaidItem = useMemo(() => {
    return paymentItemsUnPaid[0];
  }, [paymentItemsUnPaid]);

  const unPaidItemName = getPaymentName(
    {
      isRefundable: unPaidItem?.isRefundable,
      name: unPaidItem?.name,
      description: unPaidItem?.description,
      category: unPaidItem?.category,
    },
    t
  );
  const getDueTotal = () => {
    const unPaidItems = paymentItemsUnPaid;
    const resutl: any = {
      totalAmount: 0,
      paidAmount: 0,
      dataArr: [],
    };

    unPaidItems.forEach((item: any) => {
      resutl.totalAmount += item.totalAmount;
      resutl.paidAmount += item.paidAmount;
      resutl.dataArr.push({
        description: getPaymentName(
          {
            isRefundable: item?.isRefundable,
            name: item?.name,
            description: item?.description,
            category: item?.category,
          },
          t
        ),
        dueDatetime: item.dueDatetime,
      });
    });
    return resutl;
  };

  const getOverdueStatus = () => {
    const unPaidItems = paymentItemsUnPaid;
    return unPaidItems.some((item: any) => {
      return item?.status === PaymentItemStatusOptions.OVERDUE;
    });
  };

  const bookingIsPastOrCanceled = useMemo(() => {
    return status === BookingStatusOption.PAST || status === BookingStatusOption.CANCELLED;
  }, [status]);

  return (
    <div className="booking-card-payment">
      <div
        className={cn('booking-card-payment__info', {
          'booking-card-payment__info--complementaryRed': bookingIsPastOrCanceled,
        })}
      >
        {paymentItemsAllPaid && status === BookingStatusOption.RESIDENT && (
          <div>
            <Typography component="div" variant="h9">
              {t('my_bookings.congratulations')}
            </Typography>
            <Typography component="div" variant="body3">
              {isPayToPartner ? t('you_are_all_set_up') : t('my_bookings.all_paid_up')}
            </Typography>
            {isPayToPartner && (
              <Typography component="div" variant="body3">
                {t('payments_for_your_stay_are_payable_to_partner_name', {
                  partnerName: nominationPlan?.partner?.name,
                })}
              </Typography>
            )}
          </div>
        )}
        {!paymentItemsAllPaid && unPaidItem && (
          <div className="booking-card-payment__next-payment">
            <Typography component="div" variant="body3">
              {t('my_stay.next_payment')}:
            </Typography>
            {!bookingIsPastOrCanceled && (
              <div className="booking-card-payment__amount">
                {!unPaidItem?.paidAmount && (
                  <Typography variant="h7">
                    {unPaidItem?.currency}{' '}
                    {currencyformatter(unPaidItem?.totalAmount, unPaidItem?.currency)}
                  </Typography>
                )}

                {unPaidItem?.paidAmount &&
                  Number(unPaidItem?.totalAmount) !== Number(unPaidItem?.paidAmount) && (
                    <>
                      <Typography variant="h7">
                        {unPaidItem?.currency}{' '}
                        {currencyformatter(
                          unPaidItem?.totalAmount - unPaidItem?.paidAmount,
                          unPaidItem?.currency
                        )}
                      </Typography>
                      <Typography variant="body2">
                        {' '}
                        of {currencyformatter(unPaidItem?.totalAmount, unPaidItem?.currency)}
                      </Typography>
                    </>
                  )}

                {unPaidItem?.paidAmount &&
                  Number(unPaidItem?.totalAmount) === Number(unPaidItem?.paidAmount) && (
                    <Typography variant="body2">
                      {unPaidItem?.currency} {currencyformatter(0, unPaidItem?.currency)}
                    </Typography>
                  )}

                {(unPaidItem.status === PaymentItemStatusOptions.INVOICED ||
                  unPaidItem.status === PaymentItemStatusOptions.REMAINING) && (
                  <div className="booking-card-payment__amount-unpaid">
                    <div className="booking-card-payment__amount-status">
                      <Close
                        fill="var(--color-white)"
                        className="booking-card-payment__status-close"
                      />
                    </div>
                    <Typography variant="body3" color="var(--color-red_6)">
                      {t('my_bookings.unpaid')}
                    </Typography>
                  </div>
                )}
                {unPaidItem.status === PaymentItemStatusOptions.OVERDUE && (
                  <StatusButton
                    status="error"
                    name={t('status.overdue')}
                    className="booking-card-payment__status-btn"
                  />
                )}
                {unPaidItem.status === PaymentItemStatusOptions.AWAITING && (
                  <StatusButton
                    status="awaiting"
                    name={t('payment_item.status.AWAITING')}
                    className="booking-card-payment__status-btn"
                  />
                )}
              </div>
            )}
            {bookingIsPastOrCanceled && (
              <div className="booking-card-payment__amount">
                <Typography variant="h7">
                  {unPaidItem?.currency}{' '}
                  {currencyformatter(
                    getDueTotal().totalAmount - getDueTotal().paidAmount,
                    unPaidItem?.currency
                  )}
                </Typography>
                {getOverdueStatus() && (
                  <StatusButton
                    status="error"
                    name={t('status.overdue')}
                    className="booking-card-payment__status-btn"
                  />
                )}
                {!getOverdueStatus() && (
                  <div className="booking-card-payment__amount-unpaid">
                    <div className="booking-card-payment__amount-status">
                      <Close
                        fill="var(--color-white)"
                        className="booking-card-payment__status-close"
                      />
                    </div>
                    <Typography variant="body3" color="var(--color-red_6)">
                      {t('my_bookings.unpaid')}
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {!bookingIsPastOrCanceled && unPaidItem.status === PaymentItemStatusOptions.OPEN && (
              <Typography
                variant="body3"
                component="div"
                className="booking-card-payment__due-data"
              >
                {t('check_in.expected', {
                  date:
                    property?.timezone &&
                    momentTz(unPaidItem.invoiceDatetime)
                      .tz(property?.timezone)
                      .format('ddd, ll'),
                })}
                ({unPaidItemName})
              </Typography>
            )}
            {!bookingIsPastOrCanceled && unPaidItem.status !== PaymentItemStatusOptions.OPEN && (
              <Typography
                variant="body3"
                component="div"
                className="booking-card-payment__due-data"
              >
                {property?.timezone &&
                  `${t('payment_plan.table.date')}: ${momentTz(unPaidItem.dueDatetime)
                    .tz(property?.timezone)
                    .format('ddd, ll')} (${unPaidItemName})`}
              </Typography>
            )}
            {bookingIsPastOrCanceled && (
              <Typography
                variant="body3"
                component="div"
                className="booking-card-payment__due-data"
              >
                {getDueTotal().dataArr.length > 0 && `${t('payment_plan.table.date')}: `}
                {getDueTotal().dataArr.map((item: any, index: number) => {
                  return (
                    <Typography variant="body3" key={`due${index}`}>
                      {index > 0 && ' / '}
                      {property?.timezone &&
                        `${momentTz(item.dueDatetime)
                          .tz(property?.timezone)
                          .format('ddd, ll')}(${item.description})`}
                    </Typography>
                  );
                })}
              </Typography>
            )}
          </div>
        )}
      </div>
      <style jsx>
        {`
          .booking-card-payment {
            margin-top: 14px;
            &__next-payment {
              width: 100%;
            }
            :global(&__status-btn) {
              margin-left: 8px;
            }
            :global(&__due-data) {
              width: 500px;
              height: 24px;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
            &__info {
              display: flex;
              align-items: center;
              height: 96px;
              padding: 6px 0 6px 12px;
              position: relative;
              &:before {
                content: '';
                display: block;
                width: 4px;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
                border-radius: 100px;
                background-color: var(--color-red_6);
              }
              &--complementaryRed {
                &:before {
                  background-color: var(--color-blue_6);
                }
              }
            }
            &__amount {
              margin-top: 8px;
              display: flex;
              align-items: center;
            }
            :global(&__amount-total) {
              margin-left: 4px;
            }
            &__amount-unpaid {
              margin-left: 8px;
              display: flex;
              align-items: center;
            }
            &__amount-status {
              width: 16px;
              height: 16px;
              background: var(--color-red_6);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 4px;
            }
            :global(&__status-close) {
              width: 8px;
            }
          }
          @media screen and (max-width: 768px) {
            .booking-card-payment {
              :global(&__due-data) {
                width: 100%;
                height: auto;
              }
            }
          }
        `}
      </style>
    </div>
  );
};

export default BookingCardPayment;
